import { type ClassValue, clsx } from 'clsx'
import { extendTailwindMerge } from 'tailwind-merge'

const SPACING_TOKENS = [
    'no-space',
    'xx-small',
    'x-small',
    'small',
    'medium',
    'large',
    'x-large',
    'xx-large',
    'xxx-large',
    '4x-large',
    '5x-large',
    '6x-large',
    '7x-large',
    '8x-large',
    '9x-large',
    '10x-large',
    '11x-large',
] as const

// 'none' already a literal in scaleRadius(); 'medium'/'large'/'round' not t-shirt sizes
const RADIUS_TOKENS = ['medium', 'large', 'round'] as const

// 'medium' does not match isTshirtSize (/xs|sm|md|lg|xl/)
const SHADOW_TOKENS = ['medium'] as const

// 'in-out' already in default; 'emphasized' is not
const EASE_TOKENS = ['emphasized'] as const

// 'none' included so tailwind-merge treats border-t-none/border-r-none etc. as border-WIDTH
// classes (not border-color classes), preventing false conflicts with border-color utilities.
const BORDER_WIDTH_TOKENS = ['none', 'thin', 'thick'] as const

// Duration tokens: default only matches isNumber and 'initial'
const DURATION_TOKENS = ['fast', 'normal', 'slow', 'slowest'] as const

// Z-index tokens: default only matches isInteger and 'auto'
const Z_INDEX_TOKENS = [
    'base',
    'hover',
    'focus',
    'sticky',
    'fixed',
    'dropdown',
    'modal-backdrop',
    'modal',
    'popover',
    'toast',
    'tooltip',
] as const

// Custom font-size tokens are not t-shirt sizes and not recognized by tailwind-merge's default
// 'text' theme. Without registering them, cn('text-x-small', 'text-text-secondary') treats both as
// `text-*` classes and drops the font-size class — the Tag would then inherit body font-size.
const TEXT_TOKENS = [
    'xx-small',
    'x-small',
    'small',
    'medium',
    'large',
    'x-large',
    'xx-large',
    'heading-1',
    'heading-2',
    'heading-3',
] as const

// 'bold' already in tailwind-merge's default 'font-weight' theme; 'regular' and 'semi-bold' are
// custom tokens. Without registering them, cn('font-regular', 'font-bold') keeps both classes
// and the CSS cascade — not the cn() argument order — decides the winner, so isBold-style
// overrides silently no-op.
const FONT_WEIGHT_TOKENS = ['regular', 'semi-bold'] as const

const twMerge = extendTailwindMerge({
    extend: {
        theme: {
            spacing: SPACING_TOKENS,
            radius: RADIUS_TOKENS,
            shadow: SHADOW_TOKENS,
            ease: EASE_TOKENS,
            text: TEXT_TOKENS,
            'font-weight': FONT_WEIGHT_TOKENS,
        },
        classGroups: {
            'border-w': [{ border: BORDER_WIDTH_TOKENS }],
            'border-w-x': [{ 'border-x': BORDER_WIDTH_TOKENS }],
            'border-w-y': [{ 'border-y': BORDER_WIDTH_TOKENS }],
            'border-w-s': [{ 'border-s': BORDER_WIDTH_TOKENS }],
            'border-w-e': [{ 'border-e': BORDER_WIDTH_TOKENS }],
            'border-w-t': [{ 'border-t': BORDER_WIDTH_TOKENS }],
            'border-w-r': [{ 'border-r': BORDER_WIDTH_TOKENS }],
            'border-w-b': [{ 'border-b': BORDER_WIDTH_TOKENS }],
            'border-w-l': [{ 'border-l': BORDER_WIDTH_TOKENS }],
            duration: [{ duration: DURATION_TOKENS }],
            z: [{ z: Z_INDEX_TOKENS }],
        },
    },
})

export function cn(...inputs: Array<ClassValue>): string {
    return twMerge(clsx(inputs))
}
